import {
  Alert,
  Item,
  ItemDescription,
  ItemHeader,
  ItemTitle,
  ScrollArea,
  Sentinel,
  Skeleton,
  Spinner,
  TextWithDescription,
  useInfiniteScroll,
} from '@ibsys/kit';
import { FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useInfinityAttributeGroups } from '@/entities/attributes';
import { useAppNavigate } from '@/shared/lib';

export const AttributeGroupsList: FC = () => {
  const { t } = useTranslation('attributeGroups');
  const { id } = useParams<{ id?: string }>();
  const viewportRef = useRef<HTMLDivElement>(null);
  const navigate = useAppNavigate();

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfinityAttributeGroups({
    limit: 10,
  });

  const items = useMemo(() => data?.attributeGroups ?? [], [data]);
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: fetchNextPage,
    hasMore: hasNextPage,
    isLoading: isFetchingNextPage,
    threshold: 500,
    root: viewportRef,
  });

  const handleSelect = (code: string) => {
    navigate(`/attribute-groups/${code}`);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5 h-full">
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      </div>
    );
  }

  if (!items?.length) {
    return <Alert className="text-muted-foreground p-4" title={t('noGroups')} />;
  }

  return (
    <ScrollArea viewPortProps={{ ref: viewportRef }}>
      <ul className="flex flex-col gap-4">
        {items.map((item) => {
          const isActive = item.code === id;
          return (
            <li key={item.code} onClick={() => handleSelect(item.code)}>
              <Item variant={isActive ? 'muted' : 'outline'} className="hover:bg-muted cursor-pointer">
                <ItemHeader>
                  <ItemTitle>
                    <TextWithDescription
                      text={<span>{item.name}</span>}
                      description={<span className="font-mono text-sm font-medium">{item.code}</span>}
                    />
                  </ItemTitle>
                </ItemHeader>
                {item.description && (
                  <ItemDescription className="text-sm text-muted-foreground line-clamp-2">
                    {item.description}
                  </ItemDescription>
                )}
              </Item>
            </li>
          );
        })}
        <Sentinel ref={sentinelRef}>
          {isFetchingNextPage && (
            <div className="w-full flex justify-center py-6">
              <Spinner className="size-8" />
            </div>
          )}
        </Sentinel>
      </ul>
    </ScrollArea>
  );
};
